import { Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface CustomProperties {
  loginId?: string;
  userAgent?: string;
  screenResolution?: string;
  pageUrl?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AppInsightsService {
  private appInsights: ApplicationInsights;
  private isInitialized = false;
  private pageLoadStartTime: number;
  private currentLoginId: string = 'anonymous';

  constructor(private router: Router) {
    this.pageLoadStartTime = performance.now();
    
    if (environment.appInsightsInstrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.appInsightsInstrumentationKey,
          enableAutoRouteTracking: false, // We'll handle this manually for better control
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxErrorStatusText: true,
          enableCorsCorrelation: true,
          enableDebug: !environment.production,
          disableFetchTracking: false,
          correlationHeaderExcludedDomains: ['*.queue.core.windows.net']
        }
      });

      this.appInsights.loadAppInsights();
      
      // Add telemetry initializer to add custom properties to every event
      this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
        envelope.data = envelope.data || {};
        envelope.data['customProperties'] = envelope.data['customProperties'] || {};
        
        // Add common properties
        const commonProps = this.getCommonProperties();
        Object.assign(envelope.data['customProperties'], commonProps);
        
        // Add current login ID to all events
        if (this.currentLoginId) {
          envelope.data['customProperties']['loginId'] = this.currentLoginId;
        }
      });

      // Track route changes automatically
      this.setupRouteTracking();

      this.isInitialized = true;
      console.log('✅ Application Insights initialized');
    } else {
      console.warn('⚠️ Application Insights not initialized - no instrumentation key provided');
    }
  }

  /**
   * Get common properties to be included with every telemetry event
   */
  private getCommonProperties(): CustomProperties {
    return {
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      pageUrl: window.location.href,
      loginId: this.currentLoginId
    };
  }

  /**
   * Setup automatic route tracking for page views
   */
  private setupRouteTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const pageViewStartTime = performance.now();
        
        // Track page view with performance after a small delay
        setTimeout(() => {
          const loadTime = performance.now() - pageViewStartTime;
          this.trackPageViewWithPerformance(event.urlAfterRedirects, loadTime);
        }, 100);
      });
  }

  /**
   * Track page view with performance metrics
   */
  private trackPageViewWithPerformance(url: string, loadTime: number): void {
    if (!this.isInitialized) return;

    const pageName = this.getPageName(url);
    
    this.appInsights.trackPageView({
      name: pageName,
      uri: url,
      properties: {
        ...this.getCommonProperties(),
        pageLoadTime: loadTime,
        url: url,
        referrer: document.referrer
      }
    });

    // Also track as custom event for easier querying
    this.logEvent('PageView', {
      pageName: pageName,
      pageUrl: url,
      pageLoadTime: loadTime.toString(),
      referrer: document.referrer
    });
  }

  /**
   * Get friendly page name from URL
   */
  private getPageName(url: string): string {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(s => s);
    
    if (segments.length === 0) return 'Home';
    
    return segments.map(s => 
      s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')
    ).join(' > ');
  }

  /**
   * Set authenticated user context
   */
  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (this.isInitialized) {
      this.currentLoginId = userId.toLowerCase();
      this.appInsights.setAuthenticatedUserContext(userId.toLowerCase(), accountId, true);
      console.log(`✅ App Insights: User context set to "${this.currentLoginId}"`);
    } else {
      console.warn('⚠️ App Insights: Cannot set user context - not initialized');
    }
  }

  /**
   * Clear authenticated user context
   */
  clearAuthenticatedUserContext(): void {
    if (this.isInitialized) {
      console.log(`✅ App Insights: Clearing user context (was: "${this.currentLoginId}")`);
      this.currentLoginId = 'anonymous';
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  /**
   * Track initial page load with full performance metrics
   */
  trackInitialPageLoad(): void {
    if (!this.isInitialized) return;

    // Wait for page to fully load
    if (document.readyState === 'complete') {
      this.logInitialPageLoadMetrics();
    } else {
      window.addEventListener('load', () => {
        this.logInitialPageLoadMetrics();
      });
    }
  }

  /**
   * Log initial page load metrics using Performance API
   */
  private logInitialPageLoadMetrics(): void {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (perfData) {
        const metrics = {
          pageLoadTime: perfData.loadEventEnd - perfData.fetchStart,
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.fetchStart,
          domInteractive: perfData.domInteractive - perfData.fetchStart,
          dnsLookup: perfData.domainLookupEnd - perfData.domainLookupStart,
          tcpConnection: perfData.connectEnd - perfData.connectStart,
          serverResponse: perfData.responseEnd - perfData.requestStart,
          domProcessing: perfData.domComplete - perfData.domInteractive,
          transferSize: perfData.transferSize || 0,
          navigationStart: perfData.fetchStart
        };

        this.logEvent('InitialPageLoad', {
          ...this.getCommonProperties(),
          pageLoadTime: metrics.pageLoadTime.toString(),
          domContentLoaded: metrics.domContentLoaded.toString(),
          domInteractive: metrics.domInteractive.toString(),
          dnsLookup: metrics.dnsLookup.toString(),
          tcpConnection: metrics.tcpConnection.toString(),
          serverResponse: metrics.serverResponse.toString(),
          domProcessing: metrics.domProcessing.toString(),
          transferSize: metrics.transferSize.toString(),
          navigationType: perfData.type
        });

        console.log('📊 Page Load Metrics:', metrics);
      }
    }, 100);
  }

  /**
   * Track user login event with detailed information
   */
  trackUserLogin(userId: string, accountId?: string, loginMethod: string = 'MSAL'): void {
    if (!this.isInitialized) return;

    const loginTime = new Date().toISOString();
    const userIdLower = userId.toLowerCase();
    
    this.logEvent('UserLogin', {
      userId: userIdLower,
      accountId: accountId || 'N/A',
      loginMethod: loginMethod,
      loginTime: loginTime,
      loginTimestamp: Date.now().toString(),
      sessionStart: 'true'
    });

    console.log(`👤 User Login Tracked: ${userIdLower} at ${loginTime}`);
  }

  /**
   * Track user logout event
   */
  trackUserLogout(userId?: string): void {
    if (!this.isInitialized) return;

    const logoutTime = new Date().toISOString();
    const userIdLower = userId ? userId.toLowerCase() : this.currentLoginId;
    
    this.logEvent('UserLogout', {
      userId: userIdLower,
      logoutTime: logoutTime,
      logoutTimestamp: Date.now().toString(),
      sessionEnd: 'true'
    });

    console.log(`👤 User Logout Tracked: ${userIdLower} at ${logoutTime}`);
  }

  /**
   * Log HTTP request details
   */
  logHttpRequest(
    method: string,
    url: string,
    duration: number,
    statusCode: number,
    success: boolean,
    customProperties?: CustomProperties
  ): void {
    if (!this.isInitialized) return;

    const properties = {
      ...this.getCommonProperties(),
      ...customProperties,
      httpMethod: method,
      url: url,
      statusCode: statusCode.toString(),
      success: success.toString()
    };

    this.appInsights.trackDependencyData({
      id: `${method}-${url}-${Date.now()}`,
      name: `${method} ${url}`,
      duration: duration,
      success: success,
      responseCode: statusCode,
      type: 'HTTP',
      data: url,
      target: this.extractHostname(url),
      properties: properties
    });
  }

  /**
   * Log HTTP error details
   */
  logHttpError(
    method: string,
    url: string,
    status: number,
    statusText: string,
    errorMessage: string,
    errorDetails?: any,
    customProperties?: CustomProperties
  ): void {
    if (!this.isInitialized) return;

    const properties = {
      ...this.getCommonProperties(),
      ...customProperties,
      httpMethod: method,
      url: url,
      statusCode: status.toString(),
      statusText: statusText,
      errorMessage: errorMessage,
      errorDetails: JSON.stringify(errorDetails || {})
    };

    this.appInsights.trackException({
      exception: new Error(`HTTP ${status} ${statusText}: ${method} ${url}`),
      severityLevel: this.getSeverityLevel(status),
      properties: properties
    });

    // Also track as trace for easier querying
    this.appInsights.trackTrace({
      message: `HTTP Error: ${method} ${url} - ${status} ${statusText}`,
      severityLevel: this.getSeverityLevel(status),
      properties: properties
    });
  }

  /**
   * Log client-side errors (Angular component errors, JS errors, etc.)
   */
  logError(error: Error, customProperties?: CustomProperties): void {
    if (!this.isInitialized) {
      console.error('App Insights not initialized, logging to console:', error);
      return;
    }

    const properties = {
      ...this.getCommonProperties(),
      ...customProperties,
      errorName: error.name,
      errorMessage: error.message,
      stack: error.stack || ''
    };

    this.appInsights.trackException({
      exception: error,
      severityLevel: 3, // Error level
      properties: properties
    });
  }

  /**
   * Log custom events
   */
  logEvent(name: string, properties?: CustomProperties): void {
    if (!this.isInitialized) return;

    const eventProperties = {
      ...this.getCommonProperties(),
      ...properties
    };

    this.appInsights.trackEvent({
      name: name,
      properties: eventProperties
    });
  }

  /**
   * Log custom trace/message
   */
  logTrace(message: string, severityLevel: number = 1, properties?: CustomProperties): void {
    if (!this.isInitialized) return;

    const traceProperties = {
      ...this.getCommonProperties(),
      ...properties
    };

    this.appInsights.trackTrace({
      message: message,
      severityLevel: severityLevel,
      properties: traceProperties
    });
  }

  /**
   * Log page view manually
   */
  logPageView(name?: string, uri?: string, properties?: CustomProperties): void {
    if (!this.isInitialized) return;

    const pageProperties = {
      ...this.getCommonProperties(),
      ...properties
    };

    this.appInsights.trackPageView({
      name: name,
      uri: uri,
      properties: pageProperties
    });
  }

  /**
   * Flush any pending telemetry
   */
  flush(): void {
    if (this.isInitialized) {
      this.appInsights.flush();
    }
  }

  /**
   * Get current login ID
   */
  getCurrentLoginId(): string {
    return this.currentLoginId.toLocaleLowerCase();
  }

  /**
   * Extract hostname from URL
   */
  private extractHostname(url: string): string {
    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return new URL(url).hostname;
      }
      return window.location.hostname;
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get severity level based on HTTP status code
   */
  private getSeverityLevel(statusCode: number): number {
    if (statusCode >= 500) return 3; // Error
    if (statusCode >= 400) return 2; // Warning
    if (statusCode >= 300) return 1; // Informational
    return 0; // Verbose
  }
}
